'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getCookie, setCookie, deleteCookie } from 'cookies-next';
import { AppliedCouponProps, CartItemProps, CheckoutDataProps } from '@/types/Cart';
import { AcceptedTerm } from '@/types/Terms';
import { toaster, Toaster } from '@/components/ui/toaster';
import { useRouter } from 'next/navigation';
import { api } from '../services/api';
import { AuthContext } from './AuthContext';
import { ResponseError } from '@/types/Error';
import { pushDataLayerEvent, toEcommerceItem } from '@/utils/dataLayer';
import { trackMetaEvent } from '@/utils/metaPixel';
import {
  getGa4ClientId,
  getGa4SessionId,
  getMetaFbc,
  getMetaFbp,
} from '@/utils/trackingIds';
import { getStoredAttribution } from '@/utils/attribution';

const MAX_VIEWED_PRODUCTS = 10;

type CartContextProps = {
  items: CartItemProps[];
  addItem: (
    productId: string,
    quantity: number,
    productType: number,
    productOption: string,
    productOptionsLabel?: string,
  ) => Promise<void>;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  reorder: (orderId: string) => Promise<void>;
  handleUpdateCheckoutData: (data: Partial<CheckoutDataProps>) => void;
  checkoutData: CheckoutDataProps;
  handleStatus: (
    type: 'forward' | 'back',
    to: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 0,
  ) => void;
  stage: number;
  handleCheckout: (data: {
    checkoutData: CheckoutDataProps;
    acceptedTerms: AcceptedTerm[];
  }) => Promise<void>;
  cartIsLoading: boolean;
  handleValidateCart: () => Promise<void>;
  updateItem: (
    productId: string,
    quantity: number,
    productType: number,
    productOption: string,
  ) => Promise<void>;
  handleSetViewProductCookie: (productId: string) => void;
  viewedProducts: string[];
  revalidateCart: () => Promise<void>;
  freeShipping: boolean;
  coupon: AppliedCouponProps | null;
  applyCoupon: (code: string) => Promise<boolean>;
  removeCoupon: () => Promise<void>;
  couponIsLoading: boolean;
};

type CartProviderProps = {
  children: React.ReactNode;
};

export const CartContext = createContext({} as CartContextProps);

export function CartProvider({ children }: CartProviderProps) {
  const [mounted, setMounted] = useState(false);
  const [cartIsLoading, setCartIsLoading] = useState(false);
  const [items, setItems] = useState<CartItemProps[]>([]);
  // Espelha `items` para `removeItem` conseguir ler o item removido antes
  // da chamada à API, sem ficar preso ao valor de `items` no primeiro
  // render (useCallback com deps vazias).
  const itemsRef = useRef<CartItemProps[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  const [checkoutData, setCheckoutData] = useState({} as CheckoutDataProps);
  const [stage, setStage] = useState(1);
  const [viewedProducts, setViewedProducts] = useState<string[]>([]);
  const [freeShipping, setFreeShipping] = useState(false);
  const [coupon, setCoupon] = useState<AppliedCouponProps | null>(null);
  const [couponIsLoading, setCouponIsLoading] = useState(false);

  const { isAuthenticated, profile } = useContext(AuthContext);
  const { push } = useRouter();

  const handleSetCookie = useCallback((data: string) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000);

    setCookie('ido.store.cart', data, {
      secure: true,
      path: '/',
      expires,
    });
  }, []);

  const handleSetViewProductCookie = useCallback((productSlug: string) => {
    const viewedProducts = getCookie(
      'ido.store.viewed_products',
    );
    let viewedProductsArray: string[] = [];

    if (viewedProducts) {
      viewedProductsArray = JSON.parse(viewedProducts as string);
    }

    if (!viewedProductsArray.includes(productSlug)) {
      viewedProductsArray.push(productSlug);
    }

    // Limitado aos últimos vistos para não acumular indefinidamente — cada
    // entrada dispara um pedido próprio no widget "Vistos Recentemente".
    if (viewedProductsArray.length > MAX_VIEWED_PRODUCTS) {
      viewedProductsArray = viewedProductsArray.slice(-MAX_VIEWED_PRODUCTS);
    }

    const expires = new Date();
    expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000);

    setViewedProducts(viewedProductsArray);

    setCookie(
      'ido.store.viewed_products',
      JSON.stringify(viewedProductsArray),
      {
        secure: true,
        path: '/',
        expires,
      },
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCoupon(null);
    deleteCookie('ido.store.cart');
  }, []);

  const fetchCart = useCallback(
    async (auth: boolean) => {
      try {
        const cartId = getCookie('ido.store.cart');
        const response = await api.get(`/cart/${cartId || 'null'}`);

        if (response.data !== null) {
          if (!auth && response.data.cartInfo.user_id) {
            clearCart();
          } else {
            setItems(response.data.items);
            setCoupon(response.data.coupon ?? null);
            handleSetCookie(response.data.cartInfo.id);

            if (response.data.merge_notice?.length > 0) {
              const names = response.data.merge_notice
                .map((item: { product_name: string }) => item.product_name)
                .join(', ');

              toaster.create({
                type: 'warning',
                title: 'O seu carrinho anterior foi unido a este',
                description: `Não foi possível manter a quantidade total de: ${names} (stock insuficiente)`,
                duration: 8000,
              });
            }
          }
        } else {
          clearCart();
        }
      } catch {
        clearCart();
        toaster.create({
          type: 'error',
          title: `Não foi possivel obter o carrinho`,
        });
      }
    },
    [handleSetCookie, clearCart],
  );

  const addItem = useCallback(
    async (
      productId: string,
      quantity: number,
      productType: number,
      productOption: string,
      productOptionsLabel?: string,
    ) => {
      try {
        setCartIsLoading(true);

        const cartId = getCookie('ido.store.cart');

        const response = await api.post(`cart/validate-product`, {
          productId,
          quantity,
          productType,
          productOption,
          productOptionsLabel: productOptionsLabel || null,
          cart_id: cartId || null,
        });

        if (response.data) {
          setItems(response.data.items);
          handleSetCookie(response.data.cartInfo.id);

          const addedItem = (response.data.items as CartItemProps[]).find(
            (item) =>
              item.product_id === productId &&
              item.product_options === productOption,
          );
          if (addedItem?.product) {
            const ecommerceItem = toEcommerceItem(addedItem.product, quantity);
            const value = ecommerceItem.price * quantity;
            pushDataLayerEvent('add_to_cart', {
              ecommerce: { currency: 'EUR', value, items: [ecommerceItem] },
            });
            trackMetaEvent('AddToCart', {
              content_ids: [ecommerceItem.item_id],
              content_type: 'product',
              content_name: ecommerceItem.item_name,
              currency: 'EUR',
              value,
            });
          }

          toaster.create({
            type: 'success',
            title: `Artigo adicionado ao carrinho`,
          });
        }
      } catch (e) {
        const err = e as ResponseError;
        toaster.create({
          type: 'error',
          title:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : `Erro ao adicionar artigo ao carrinho`,
        });
      } finally {
        setCartIsLoading(false);
      }
    },
    [handleSetCookie],
  );

  const updateItem = useCallback(
    async (
      productId: string,
      quantity: number,
      productType: number,
      productOption: string,
    ) => {
      try {
        setCartIsLoading(true);

        const cartId = getCookie('ido.store.cart');

        if (!cartId) return;

        const previousItem = itemsRef.current.find(
          (item) =>
            item.product_id === productId &&
            item.product_options === productOption,
        );

        const response = await api.put(`cart/validate-product/${cartId}`, {
          productId,
          quantity,
          productType,
          productOption,
        });

        if (response.data) {
          setItems(response.data.items);
          handleSetCookie(response.data.cartInfo.id);

          const updatedItem = (response.data.items as CartItemProps[]).find(
            (item) =>
              item.product_id === productId &&
              item.product_options === productOption,
          );
          const delta = quantity - (previousItem?.quantity ?? 0);
          if (delta !== 0 && updatedItem?.product) {
            const ecommerceItem = toEcommerceItem(
              updatedItem.product,
              Math.abs(delta),
            );
            const value = ecommerceItem.price * Math.abs(delta);
            if (delta > 0) {
              pushDataLayerEvent('add_to_cart', {
                ecommerce: { currency: 'EUR', value, items: [ecommerceItem] },
              });
              trackMetaEvent('AddToCart', {
                content_ids: [ecommerceItem.item_id],
                content_type: 'product',
                content_name: ecommerceItem.item_name,
                currency: 'EUR',
                value,
              });
            } else {
              pushDataLayerEvent('remove_from_cart', {
                ecommerce: { currency: 'EUR', value, items: [ecommerceItem] },
              });
            }
          }

          toaster.create({
            type: 'success',
            title: `Artigo do carrinho atualizado`,
          });
        }
      } catch (e) {
        const err = e as ResponseError;
        toaster.create({
          type: 'error',
          title:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : `Erro ao atualizar artigo do carrinho`,
        });
      } finally {
        setCartIsLoading(false);
      }
    },
    [handleSetCookie],
  );

  const removeItem = useCallback(async (itemId: string) => {
    const removedItem = itemsRef.current.find((item) => item.id === itemId);

    try {
      const response = await api.delete(`/cart/delete-item/${itemId}`);

      if (response.data !== null) {
        setItems(response.data.items);

        if (removedItem?.product) {
          const ecommerceItem = toEcommerceItem(
            removedItem.product,
            removedItem.quantity,
          );
          const value = ecommerceItem.price * removedItem.quantity;
          pushDataLayerEvent('remove_from_cart', {
            ecommerce: { currency: 'EUR', value, items: [ecommerceItem] },
          });
        }

        toaster.create({
          type: 'success',
          title: `Artigo removido do carrinho`,
        });
      }
    } catch {
      toaster.create({
        type: 'error',
        title: `Erro ao remover artigo do carrinho`,
      });
    }
  }, []);

  const reorder = useCallback(
    async (orderId: string) => {
      try {
        setCartIsLoading(true);

        const cartId = getCookie('ido.store.cart');

        const response = await api.post(`/order/${orderId}/reorder`, {
          cart_id: cartId || null,
        });

        setItems(response.data.items);
        handleSetCookie(response.data.cartInfo.id);

        setCheckoutData((prevState) => ({
          ...prevState,
          payment: response.data.payment,
          deliveryType: response.data.delivery_type,
          deliveryAddress: response.data.delivery_address,
          billingAddress: response.data.billing_address,
          store: response.data.store,
        }));

        if (response.data.skipped?.length > 0) {
          const names = response.data.skipped
            .map((s: { product_name: string }) => s.product_name)
            .join(', ');

          toaster.create({
            type: 'warning',
            title: 'Alguns artigos já não estão disponíveis',
            description: `Não foi possível voltar a adicionar: ${names}`,
            duration: 8000,
          });
        }

        push('/Checkout');
      } catch (e) {
        const err = e as ResponseError;
        toaster.create({
          type: 'error',
          title:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Não foi possível encomendar novamente',
        });
      } finally {
        setCartIsLoading(false);
      }
    },
    [handleSetCookie, push],
  );

  const handleUpdateCheckoutData = useCallback(
    (data: Partial<CheckoutDataProps>) => {
      setCheckoutData((prevState) => ({ ...prevState, ...data }));
    },
    [],
  );

  const handleStatus = useCallback(
    (type: 'forward' | 'back', to: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 0) => {
      if (type === 'back') {
        if (profile) {
          setStage((prevState) => (prevState > 2 ? prevState - 1 : prevState));
        } else {
          setStage((prevState) => (prevState > 1 ? prevState - 1 : prevState));
        }
      } else {
        setStage(to);
      }
    },
    [profile],
  );

  const handleValidateCart = useCallback(async () => {
    try {
      const cartId = getCookie('ido.store.cart');

      if (!cartId) push('/');

      const response = await api.get(`/cart/validate/${cartId}`);

      setItems(response.data.items);
      setFreeShipping(response.data.free_shipping ?? false);
      setCoupon(response.data.coupon ?? null);

      // O carrinho de convidado pode ter sido juntado ao da conta durante
      // esta validação (login feito dentro do checkout) — o id devolvido
      // já é o do carrinho resultante, por isso o cookie tem de acompanhar.
      handleSetCookie(response.data.cartInfo.id);

      if (response.data.coupon_removed_reason) {
        toaster.create({
          type: 'warning',
          title: 'Código de desconto removido',
          description: response.data.coupon_removed_reason,
        });
      }

      if (response.data.merge_notice?.length > 0) {
        const names = response.data.merge_notice
          .map((item: { product_name: string }) => item.product_name)
          .join(', ');

        toaster.create({
          type: 'warning',
          title: 'O seu carrinho anterior foi unido a este',
          description: `Não foi possível manter a quantidade total de: ${names} (stock insuficiente)`,
          duration: 8000,
        });
      }
    } catch {
      toaster.create({
        type: 'error',
        title: `Não foi possivel validar o carrinho`,
      });
      setStage(1);
      push('/');
    }
  }, [push, handleSetCookie]);

  const applyCoupon = useCallback(async (code: string) => {
    try {
      setCouponIsLoading(true);
      const cartId = getCookie('ido.store.cart');

      const response = await api.post('/cart/coupon', {
        cart_id: cartId,
        code,
      });

      setCoupon(response.data);
      toaster.create({
        type: 'success',
        title: 'Código de desconto aplicado',
      });
      return true;
    } catch (e) {
      const err = e as ResponseError;
      toaster.create({
        type: 'error',
        title:
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Código de desconto inválido',
      });
      return false;
    } finally {
      setCouponIsLoading(false);
    }
  }, []);

  const removeCoupon = useCallback(async () => {
    try {
      setCouponIsLoading(true);
      const cartId = getCookie('ido.store.cart');

      await api.delete(`/cart/coupon/${cartId}`);
      setCoupon(null);
    } catch {
      toaster.create({
        type: 'error',
        title: 'Não foi possível remover o código de desconto',
      });
    } finally {
      setCouponIsLoading(false);
    }
  }, []);

  const handleCheckout = useCallback(
    async (data: {
      checkoutData: CheckoutDataProps;
      acceptedTerms: AcceptedTerm[];
    }) => {
      try {
        setCartIsLoading(true);

        const cartId = getCookie('ido.store.cart');

        const response = await api.post('/checkout', {
          checkoutData: {
            ...data.checkoutData,
            ga_client_id: getGa4ClientId(),
            ga_session_id: getGa4SessionId(),
            fbp: getMetaFbp(),
            fbc: getMetaFbc(),
            // De onde veio o cliente (anúncio, newsletter…), capturado por
            // AttributionCapture na primeira página que viu.
            ...getStoredAttribution(),
          },
          cart_id: cartId,
          // Condições aceites na checkbox do checkout (documento + versão),
          // validadas e guardadas pelo backend com a encomenda.
          accepted_terms: data.acceptedTerms,
        });

        if (response.data.paymentProvider === 'PAYPAL') {
          const responsePaypal = await api.post('/checkout/payment/paypal', {
            order_id: response.data.order_id,
          });

          if (responsePaypal.data && responsePaypal.data.aproval_url) {
            window.location.href = responsePaypal.data.aproval_url;
          } else {
            toaster.create({
              type: 'error',
              title: `Erro ao redirecionar para o PayPal, tente novamente mais tarde ou entre em contacto connosco.`,
            });
          }
          return;
        } else {
          push(`/encomenda/${response.data.order_id}?startpayment=true`);
        }

        await clearCart();
        await fetchCart(isAuthenticated);
      } catch (error) {
        setCartIsLoading(false);
        // Condições não aceites ou atualizadas entretanto — o backend explica
        // o que fazer; nos restantes erros mantém-se a mensagem genérica.
        const response = (error as { response?: { data?: { message?: string; field?: string } } }).response;
        toaster.create({
          type: 'error',
          title:
            response?.data?.field === 'accepted_terms' && response.data.message
              ? response.data.message
              : `Erro ao finalizar a encomenda, tente novamente mais tarde ou entre em contacto connosco.`,
        });
      }
    },
    [fetchCart, isAuthenticated, clearCart, push],
  );

  useEffect(() => {
    setMounted(true);
    // 🔥 Carregar carrinho do localStorage APENAS no cliente
    if (typeof window !== 'undefined') {
      // restaurar carrinho aqui
    }
  }, []);

  useEffect(() => {
    fetchCart(isAuthenticated);
  }, [fetchCart, isAuthenticated]);

  // O "envio grátis" do método escolhido é calculado pelo backend com o
  // valor do carrinho no momento da escolha (passo do envio). Se o carrinho
  // mudar depois (ex.: remover artigos e ficar abaixo do valor mínimo), o
  // valor guardado ficava desatualizado — o resumo mostrava "Grátis" e o
  // backend cobrava os portes. Sempre que o carrinho muda, volta-se a
  // perguntar ao backend e atualiza-se o método escolhido.
  const cartSignature = items
    .map((item) => `${item.id}:${item.quantity}:${item.total_price_tax}`)
    .join('|');
  const selectedShipmentId = checkoutData.shipmentMethod?.id;
  const deliveryCountryCode = checkoutData.deliveryAddress?.country_code ?? null;

  useEffect(() => {
    if (!selectedShipmentId) return;
    let cancelled = false;

    const refreshShipment = async () => {
      try {
        const cartId = getCookie('ido.store.cart');
        const response = await api.get<
          { id: string; price: number; free_shipping: boolean }[]
        >('/shipments/list/simple', {
          params: { country_code: deliveryCountryCode, cart_id: cartId },
        });
        if (cancelled) return;

        const current = response.data.find(
          (shipment) => String(shipment.id) === String(selectedShipmentId),
        );
        // Método deixou de estar disponível — o backend recusa a encomenda
        // e o cliente escolhe outro; aqui não há nada a atualizar.
        if (!current) return;

        setCheckoutData((prev) => {
          const method = prev.shipmentMethod;
          if (
            !method ||
            String(method.id) !== String(current.id) ||
            (method.free_shipping === current.free_shipping &&
              method.price === current.price)
          ) {
            return prev;
          }
          return {
            ...prev,
            shipmentMethod: {
              ...method,
              price: current.price,
              free_shipping: current.free_shipping,
            },
          };
        });
      } catch {
        // Silencioso — o backend volta a calcular os portes ao criar a
        // encomenda, por isso o valor cobrado está sempre correto.
      }
    };

    refreshShipment();
    return () => {
      cancelled = true;
    };
  }, [cartSignature, selectedShipmentId, deliveryCountryCode]);

  useEffect(() => {
    if (profile) {
      handleUpdateCheckoutData({
        userType: 'user',
        deliveryAddress: null,
        deliveryType: null,
        billingAddress: null,
        shipmentMethod: null,
        store: null,
      });
      handleStatus('forward', 2);
    } else {
      handleUpdateCheckoutData({
        userType: null,
        deliveryAddress: null,
        deliveryType: null,
        billingAddress: null,
        shipmentMethod: null,
        store: null,
      } as CheckoutDataProps);
      handleStatus('forward', 1);
    }
  }, [profile, handleUpdateCheckoutData, handleStatus]);

  useEffect(() => {
    const viewedProductsCookie = getCookie(
      'ido.store.viewed_products',
    );
    if (viewedProductsCookie) {
      const viewedProductsArray: string[] = JSON.parse(
        viewedProductsCookie as string,
      );
      setViewedProducts(viewedProductsArray.slice(-MAX_VIEWED_PRODUCTS));
    }
  }, []);

  if (!mounted) {
    return (
      <CartContext.Provider
        value={{
          items: [],
          addItem: async () => {},
          removeItem: () => {},
          clearCart: () => {},
          reorder: async () => {},
          handleUpdateCheckoutData: () => {},
          checkoutData: {} as CheckoutDataProps,
          handleStatus: () => {},
          stage: 1,
          handleCheckout: async () => {},
          cartIsLoading: false,
          handleValidateCart: async () => {},
          updateItem: async () => {},
          handleSetViewProductCookie: () => {},
          viewedProducts: [],
          revalidateCart: async () => {},
          freeShipping: false,
          coupon: null,
          applyCoupon: async () => false,
          removeCoupon: async () => {},
          couponIsLoading: false,
        }}
      >
        {children}
      </CartContext.Provider>
    );
  }

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        clearCart,
        reorder,
        handleUpdateCheckoutData,
        checkoutData,
        handleStatus,
        stage,
        handleCheckout,
        cartIsLoading,
        handleValidateCart,
        updateItem,
        handleSetViewProductCookie,
        viewedProducts,
        revalidateCart: () => fetchCart(isAuthenticated),
        freeShipping,
        coupon,
        applyCoupon,
        removeCoupon,
        couponIsLoading,
      }}
    >
      <Toaster />

      {children}
    </CartContext.Provider>
  );
}
